// SSE client for /api/v1/events/stream — the one live-update connection.
// Uses fetch + ReadableStream so we can attach the bearer token + business
// header (EventSource doesn't support custom headers). Two frame kinds ride
// the connection: `notification` (Notification rows — bell, Today, chat
// resume), `domain` (AgentEvent rows — pure "something changed" signals
// for query invalidation) and `run_step` (an agent run took a step). Tracks the server's `id:` field and resends it as
// `Last-Event-ID` on reconnect so a dropped connection replays what it missed.

import { fetchWithAuthRetry, getBaseUrl, tokenManager } from './client'
import { useAuthStore } from '@/stores/useAuthStore'
import type { Notification } from '@/mocks/data/types'

export interface DomainEvent {
  id: string
  business_id: string
  entity: string
  entity_id: string
  op: string
  payload: Record<string, unknown>
  created_at: string | null
}

/** An agent run took a step (its steps live on the run; this frame only says which). */
export interface RunStepEvent {
  run_id: string
  step_count: number
  current_step: string | null
  production_brief_id: string | null
}

export type RunStepStreamHandler = (event: RunStepEvent) => void
export type NotificationStreamHandler = (notification: Notification) => void
export type DomainEventStreamHandler = (event: DomainEvent) => void

interface SubscribeOptions {
  /** Invoked once per delivered Notification frame. */
  onNotification?: NotificationStreamHandler
  /** Invoked once per delivered AgentEvent (domain) frame. */
  onDomainEvent?: DomainEventStreamHandler
  /** Invoked once per agent run step (`run_step`) frame. */
  onRunStep?: RunStepStreamHandler
  /** Invoked when the server accepted the connection (headers received). */
  onOpen?: () => void
  /** Invoked when the stream failed or the server closed it. */
  onError?: (err: unknown) => void
}

/**
 * Open a long-lived SSE connection to the live-update event stream.
 *
 * Returns a disposer that aborts the underlying fetch. The caller is
 * responsible for invoking it on unmount / token change.
 */
export function subscribeEventStream({
  onNotification,
  onDomainEvent,
  onRunStep,
  onOpen,
  onError,
}: SubscribeOptions): () => void {
  const controller = new AbortController()
  let lastEventId: string | null = null

  const connect = () => {
    void runStream(controller, {
      onNotification,
      onDomainEvent,
      onRunStep,
      onOpen,
      onError,
      getLastEventId: () => lastEventId,
      setLastEventId: (id) => {
        lastEventId = id
      },
    })
  }
  connect()

  return () => {
    controller.abort()
  }
}

async function runStream(
  controller: AbortController,
  opts: {
    onNotification?: NotificationStreamHandler
    onDomainEvent?: DomainEventStreamHandler
    onRunStep?: RunStepStreamHandler
    onOpen?: () => void
    onError?: (err: unknown) => void
    getLastEventId: () => string | null
    setLastEventId: (id: string) => void
  }
): Promise<void> {
  try {
    const token = tokenManager.getAccessToken()
    if (!token) return

    const user = useAuthStore.getState().user
    const businessId = (user as Record<string, unknown> | null)?.default_business_id as
      | string
      | undefined

    // A 401 lands on the response headers before any body bytes, so retrying
    // the initial connect after a refresh loses no frames. This is retry, not
    // reconnect — a mid-stream drop is still the caller's problem.
    const response = await fetchWithAuthRetry((attemptToken) => {
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
      }
      if (attemptToken) {
        headers['Authorization'] = `Bearer ${attemptToken}`
      }
      if (businessId) {
        headers['X-Business-ID'] = businessId
      }
      const lastEventId = opts.getLastEventId()
      if (lastEventId) {
        headers['Last-Event-ID'] = lastEventId
      }
      return fetch(`${getBaseUrl()}/events/stream`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      })
    }, controller.signal)

    if (!response.ok || !response.body) {
      opts.onError?.(new Error(`stream failed: HTTP ${response.status}`))
      return
    }

    opts.onOpen?.()
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        opts.onError?.(new Error('stream closed'))
        break
      }
      buffer += decoder.decode(value, { stream: true })

      // SSE frames are separated by a blank line. Process complete frames only.
      let frameEnd: number
      while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, frameEnd)
        buffer = buffer.slice(frameEnd + 2)
        parseFrame(frame, opts)
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return
    opts.onError?.(err)
  }
}

function parseFrame(
  frame: string,
  opts: {
    onNotification?: NotificationStreamHandler
    onDomainEvent?: DomainEventStreamHandler
    onRunStep?: RunStepStreamHandler
    onError?: (err: unknown) => void
    setLastEventId: (id: string) => void
  }
): void {
  let eventType: string | null = null
  let id: string | null = null
  const dataLines: string[] = []

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '')
    if (line.length === 0 || line.startsWith(':')) continue
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    } else if (line.startsWith('id:')) {
      id = line.slice(3).trim()
    }
  }

  if (id) opts.setLastEventId(id)
  if (dataLines.length === 0) return

  try {
    if (eventType === 'notification') {
      opts.onNotification?.(JSON.parse(dataLines.join('\n')) as Notification)
    } else if (eventType === 'run_step') {
      opts.onRunStep?.(JSON.parse(dataLines.join('\n')) as RunStepEvent)
    } else if (eventType === 'domain') {
      opts.onDomainEvent?.(JSON.parse(dataLines.join('\n')) as DomainEvent)
    }
  } catch (err) {
    opts.onError?.(err)
  }
}
